import { PureComponent } from "react";
import PropTypes from "prop-types";
import { stringify } from "query-string";
import withViewport from "../Viewport/withViewport";
import { withTranslation } from "@template/components/translation";
import styles from "./index.css";
import stylesDeferred from "./deferred.css";
import { getMyAccountBaseUrl } from "../../helpers/externalLinks";
import {
  MY_ACCOUNT,
  MY_ORDERS,
  MY_RETURNS,
  MY_ACCOUNT_DEFAULT,
  MY_ACCOUNT_CHRISTMAS_LOGGEDIN,
  MY_ACCOUNT_CHRISTMAS_LOGGEDOUT,
  MY_ACCOUNT_EASTER_LOGGEDIN,
  MY_ACCOUNT_EASTER_LOGGEDOUT,
  MY_ACCOUNT_HALLOWEEN_LOGGEDIN,
  MY_ACCOUNT_HALLOWEEN_LOGGEDOUT,
  MY_ACCOUNT_STPATRICKS_LOGGEDIN,
  MY_ACCOUNT_STPATRICKS_LOGGEDOUT
} from "./constants";
import {
  CHRISTMAS,
  STPATRICKS,
  HALLOWEEN,
  EASTER
} from "@template/helpers/eventConstants";
import getFeature from "@template/helpers/getFeature";
import classnames from "classnames/bind";
import { isWindowDefined } from "@src/helpers/url";
import { asosGlobalNamespace, sdkGlobalNamespace } from "@template/constants";

class MyAccountLinks extends PureComponent {
  static propTypes = {
    countryCode: PropTypes.string.isRequired,
    defaultStoreUrl: PropTypes.string.isRequired,
    focusable: PropTypes.bool,
    formatTranslation: PropTypes.func.isRequired,
    isLoggedIn: PropTypes.bool.isRequired,
    isVisible: PropTypes.bool,
    keyStoreDataversion: PropTypes.string.isRequired,
    lang: PropTypes.string.isRequired,
    queryParams: PropTypes.object,
    storeCode: PropTypes.string.isRequired,
    currentEvent: PropTypes.string,
    showMyAccountNotification: PropTypes.bool
  };

  constructor(props) {
    super(props);
    this.state = {
      myReturnsToggle: false
    };
  }

  componentDidMount() {
    getFeature("acc-returnsenabledcountries").then(myReturnsToggle => {
      this.setState({ myReturnsToggle: myReturnsToggle.isEnabled() });
    });
  }

  buildLinks() {
    const {
      countryCode,
      formatTranslation,
      keyStoreDataversion,
      lang,
      queryParams = {},
      storeCode,
      showMyAccountNotification
    } = this.props;

    const queryString =
      "?" +
      stringify({
        ...queryParams,
        lang,
        store: storeCode,
        country: countryCode,
        keyStoreDataversion
      });

    const baseUrl = getMyAccountBaseUrl();

    const myReturnsLink = this.state.myReturnsToggle
      ? [
          {
            id: MY_RETURNS,
            href: `${baseUrl}/returns${queryString}`,
            icon: stylesDeferred.MyReturns
          }
        ]
      : [];

    const links = [
      {
        id: MY_ACCOUNT,
        href: baseUrl + queryString,
        icon: this.getMyAccountIcon(),
        hasNotificationWaiting: showMyAccountNotification,
        notificationAriaLabel: formatTranslation(
          "desktop_my_account_dropdown_cta"
        )
      },
      {
        id: MY_ORDERS,
        href: `${baseUrl}/orders${queryString}`,
        icon: stylesDeferred.MyOrders
      },
      ...myReturnsLink
    ];

    return links;
  }

  getMyAccountIcon = () => {
    const { isLoggedIn, currentEvent } = this.props;

    const eventIconsAccount = [
      {
        event: CHRISTMAS,
        loggedIn: MY_ACCOUNT_CHRISTMAS_LOGGEDIN,
        loggedOut: MY_ACCOUNT_CHRISTMAS_LOGGEDOUT
      },
      {
        event: STPATRICKS,
        loggedIn: MY_ACCOUNT_STPATRICKS_LOGGEDIN,
        loggedOut: MY_ACCOUNT_STPATRICKS_LOGGEDOUT
      },
      {
        event: EASTER,
        loggedIn: MY_ACCOUNT_EASTER_LOGGEDIN,
        loggedOut: MY_ACCOUNT_EASTER_LOGGEDOUT
      },
      {
        event: HALLOWEEN,
        loggedIn: MY_ACCOUNT_HALLOWEEN_LOGGEDIN,
        loggedOut: MY_ACCOUNT_HALLOWEEN_LOGGEDOUT
      }
    ];

    const activeEvent = eventIconsAccount.find(
      eventIcon => eventIcon.event === currentEvent
    );

    if (!activeEvent) {
      const defaultIcon = MY_ACCOUNT_DEFAULT;

      return defaultIcon;
    }

    const eventIcon = isLoggedIn ? activeEvent.loggedIn : activeEvent.loggedOut;

    return eventIcon;
  };

  render() {
    const { formatTranslation, isVisible, isLoggedIn } = this.props;

    if (!isVisible) return null;

    const identitySdk =
      isWindowDefined() &&
      window[asosGlobalNamespace] !== undefined &&
      window[asosGlobalNamespace][sdkGlobalNamespace] !== undefined &&
      window[asosGlobalNamespace][sdkGlobalNamespace].identity;

    return (
      <ul className={styles.container}>
        {this.buildLinks().map(
          ({
            id,
            href,
            icon,
            hasNotificationWaiting,
            notificationAriaLabel
          }) => {
            const linkClasses = classnames(styles.link, {
              [styles.hasNotificationWaiting]: hasNotificationWaiting
            });
            return (
              <li key={id}>
                {hasNotificationWaiting ? (
                  <a
                    href={href}
                    className={linkClasses}
                    data-testid={`${id.toLowerCase()}-link`}
                    onClick={() =>
                      localStorage.setItem("clearNotificationWaiting", "true")
                    }
                    aria-label={hasNotificationWaiting && notificationAriaLabel}
                  >
                    <span className={icon} />
                    {formatTranslation(`myaccountlinks_${id.toLowerCase()}`)}
                  </a>
                ) : (
                  <a
                    href={href}
                    className={linkClasses}
                    data-testid={`${id.toLowerCase()}-link`}
                  >
                    <span className={icon} />
                    {formatTranslation(`myaccountlinks_${id.toLowerCase()}`)}
                  </a>
                )}
              </li>
            );
          }
        )}
        {isLoggedIn && (
          <li>
            <button
              className={classnames(styles.link, styles.signOutLink)}
              onClick={e => {
                e.preventDefault();
                identitySdk && identitySdk.logout();
              }}
              data-testid="signout-link"
            >
              {formatTranslation("myaccountlinks_sign_out_label")}
            </button>
          </li>
        )}
      </ul>
    );
  }
}

export default withViewport(withTranslation(MyAccountLinks));
